import {
    Component,
    Input,
    NgZone,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { CmpsDialog } from '@cmps/ng-uikit';
import { tap } from 'rxjs/operators';

import { KpiType } from '../../../../coaching-insight/insight-widget/model/kpi-type.model';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../../common/take-until-destroy.decorator';
import { Sort } from '../../../../insight/experimental.grid';
import { StateToProps } from '../../../../store';
import { useDispatcher, UseStore } from '../../../../store.decorator';
import { useSelector2 } from '../../../store-use-selector';
import { ViewRow, wpAddDataRowParam, WpDataRowParam } from '../core';
import {
    Column,
    ExperimentalNgGridComponent,
    GridNode,
    RowNode,
    SortOrder,
} from '../grid';

import { CommitmentDetailsDialogComponent } from './component/commitment-details-dialog/commitment-details-dialog.component';
import { InfoDialogComponent } from './component/info-dialog/info-dialog.component';
import { setRowExpanded } from './function/create-row-values.function';
import {
    createRows,
    createTableData,
} from './function/create-table-data.function';
import { TranslationKeyMap } from './main.i18n';
import { mainReducer } from './main.reducer';
import { MainState } from './main.state';
import { ColumnId } from './model/column-id.model';
import { CommitmentDetailsDialogColumnId } from './model/commitment-details-dialog/commitment-details-dialog-column-id.model';
import { CommitmentDetailsDialogData } from './model/commitment-details-dialog/commitment-details-dialog-data.model';
import { TableData } from './model/table-data.model';
import { expandRow, expandRowCancel } from './state/expand-row.action';
import { reload } from './state/reload.action';
import { sortRows } from './state/sort-rows.action';

@Component({
    selector: 'cmps-coaching-wp-main',
    templateUrl: './main.component.html',
    host: {
        class: 'cmps-coaching-wp-main',
    },
})
@TakeUntilDestroy()
@UseStore<MainState>('coachingInsight.wp.main', mainReducer)
export class MainComponent
    implements OnChanges, OnInit, StateToProps<MainState>
{
    state: MainState;

    @ViewChild(ExperimentalNgGridComponent)
    private readonly _grid: ExperimentalNgGridComponent;

    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _ColumnId = ColumnId;

    /** @private */
    _SortOrder = SortOrder;

    @Input() userId: number;

    /** @private */
    _tableData: TableData;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _router: Router,
        private readonly _dialog: CmpsDialog,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (this.state == null) {
            return;
        }

        if (changes?.userId != null) {
            useDispatcher(this, reload({ userId: this.userId }));
        }
    }

    ngOnInit(): void {
        useSelector2<ViewRow[]>(
            (state) => (state?.coachingInsight?.wp?.main as MainState)?.rows,
        )
            .pipe(
                tap(([_, state]) => {
                    this._tableData = createTableData(
                        state,
                        this.state.sortOptions,
                        this.state.config,
                        this.state.table,
                        this.state.selectedDataRange?.evaluationRanges,
                        this.state.selectedEvalRange,
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useSelector2<any[]>(
            (state) =>
                (state?.coachingInsight?.wp?.main as MainState)?.otherRows,
        )
            .pipe(
                tap(([_, state]) => {
                    try {
                        const parentRow = this._grid?.renderer.rows.find(
                            (e) => e.def.id === state[0].rowId,
                        );
                        const rows = createRows(
                            state[0].rows,
                            this._tableData.rendererOpts.columns,
                            this.state.config,
                            this.state.table,
                            this.state.selectedDataRange?.evaluationRanges,
                            this.state.selectedEvalRange,
                        );
                        void parentRow
                            ?.setChildren(rows)
                            .then(() => this._grid?.onResize());
                    } catch (err) {
                        console.log(err);
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useDispatcher(this, reload({ userId: this.userId }));
    }

    /** @private */
    _onDataRangeChange(dataRangeIdEvt: Event): void {
        const dataRangeId = parseInt(
            (dataRangeIdEvt.target as HTMLOptionElement).value,
        );
        useDispatcher(
            this,
            reload({
                userId: this.userId,
                dataRangeId,
                evalRangeId: this.state.dataRanges.find(
                    (e) => e.dataRangeId === dataRangeId,
                )?.evaluationRanges?.[0]?.evaluationRangeIsOaMetricId,
                drillPathId: this.state.selectedDrillPathId,
            }),
        );
    }

    /** @private */
    _onEvalRangeChange(evalRangeIdEvt: Event): void {
        useDispatcher(
            this,
            reload({
                userId: this.userId,
                dataRangeId: this.state.selectedDataRange?.dataRangeId,
                evalRangeId: parseInt(
                    (evalRangeIdEvt.target as HTMLOptionElement).value,
                ),
                drillPathId: this.state.selectedDrillPathId,
            }),
        );
    }

    /** @private */
    _onDrillPathChange(drillPathIdEvt: Event): void {
        useDispatcher(
            this,
            reload({
                userId: this.userId,
                dataRangeId: this.state.selectedDataRange?.dataRangeId,
                evalRangeId:
                    this.state.selectedEvalRange?.evaluationRangeIsOaMetricId,
                drillPathId: (drillPathIdEvt.target as HTMLOptionElement).value,
            }),
        );
    }

    /** @private */
    _onResetGridClick(): void {
        useDispatcher(
            this,
            reload({
                userId: this.userId,
                dataRangeId: this.state.selectedDataRange?.dataRangeId,
                evalRangeId:
                    this.state.selectedEvalRange?.evaluationRangeIsOaMetricId,
                drillPathId: this.state.selectedDrillPathId,
                reset: true,
            }),
        );
    }

    /** @private */
    _onColumnSortClick(c: Column): void {
        this._ngZone.run(() => {
            useDispatcher(this, sortRows({ columnId: c.id }));

            // sortRows() has no side effect, so this is fine
            const determineDepth = (n: RowNode): number => {
                let depth = 0;
                let parent: any = n.parent;
                while (parent != null) {
                    depth++;
                    parent = parent.parent;
                }
                return depth;
            };
            const maxDepthParentDepth =
                this._grid.gridNode.reduce<number>((p, c) => {
                    return Math.max(determineDepth(c), p);
                }, 0) - 1;
            const sortDefs: Sort[] = [this.state.sortOptions];
            if (this.state.sortOptions.columnId !== ColumnId.LEFT) {
                sortDefs.push({
                    columnId: ColumnId.LEFT,
                    order: SortOrder.ASC,
                });
            }
            if (maxDepthParentDepth >= 0) {
                this._grid?.gridNode
                    .filter((n) => {
                        return determineDepth(n) === maxDepthParentDepth;
                    })
                    .forEach((n) => {
                        void n.sort(sortDefs).then(() => {
                            void this._grid?.renderer.layout();
                        });
                    });
            } else {
                void this._grid?.gridNode.sort(sortDefs).then(() => {
                    void this._grid?.renderer.layout();
                });
            }
        });
    }

    /** @private */
    _onRowClick(r: RowNode): void {
        this._ngZone.run(() => {
            // row cache row children array may not have ref to actual row. get it here
            const ar = this?._grid.renderer.rows.find(
                (e) => e.def.id === r.def.id,
            );

            if (ar.context.ctx.values.canExpand !== true) {
                return;
            }

            if (ar.context.ctx.values.isExpanded === true) {
                useDispatcher(this, expandRowCancel({ rowId: ar.def.id }));

                setRowExpanded(ar.def, false);
                ar.detectChanges();
                void ar.removeChildren().then(() => this._grid?.onResize());

                return;
            }

            setRowExpanded(ar.def, true);
            ar.detectChanges();

            const viewRow = ar.context.ctx.data as ViewRow;
            const drillPath = this.state.config.drillPaths.find(
                (e) => e.identifier === this.state.table.drillPathIdentifier,
            );
            const activeSegment = drillPath?.segments?.find(
                (e) => e.identifier === viewRow.segmentIdentifier,
            );
            const nextSegment = drillPath?.segments?.find(
                (e) => e.index > activeSegment?.index,
            );

            let parentRowParams: WpDataRowParam[] = [];
            let pr: RowNode | GridNode = ar;
            do {
                const vr = pr.context?.ctx?.data as ViewRow;
                if (vr == null) {
                    break;
                }
                parentRowParams = [...vr.rowParams, ...parentRowParams];
                /* skip root row */
                if (pr.parent?.parent == null) {
                    break;
                }
                pr = pr.parent;
            } while (pr != null);

            const nextParentRowParams = wpAddDataRowParam(
                parentRowParams,
                viewRow,
            );

            useDispatcher(
                this,
                expandRow({
                    userId: this.userId,
                    rowId: r.def.id,
                    drillPathIdentifier: this.state.table.drillPathIdentifier,
                    segmentIdentifier: nextSegment.identifier,
                    paramsAtSegment: {
                        parentRowParams: nextParentRowParams,
                        rootParams: this.state.table.rows[0].rowParams,
                    },
                }),
            );
        });
    }

    /** @private */
    _onLoadUserClick(userId: number): void {
        void this._router.navigate(['pulse'], {
            queryParams: {
                userId,
                kpiTypeId: KpiType.WINNING_PERCENTAGE,
            },
            queryParamsHandling: 'merge',
        });
    }

    /** @private */
    _onViewAssessedCommitmentsClick(row: ViewRow): void {
        const data: CommitmentDetailsDialogData = {
            columns: [
                CommitmentDetailsDialogColumnId.DIRECT_REPORT,
                CommitmentDetailsDialogColumnId.START_DATE,
                CommitmentDetailsDialogColumnId.DAYS_ACTIVE,
                CommitmentDetailsDialogColumnId.METRIC,
                CommitmentDetailsDialogColumnId.BEHAVIOUR,
                CommitmentDetailsDialogColumnId.STATE,
                CommitmentDetailsDialogColumnId.CALCULATION_TYPE,
                CommitmentDetailsDialogColumnId.IMPROVEMENT_PERCENTAGE,
                CommitmentDetailsDialogColumnId.WINNING_THRESHOLD,
                CommitmentDetailsDialogColumnId.INITIAL_VALUE,
                CommitmentDetailsDialogColumnId.CURRENT_VALUE,
                CommitmentDetailsDialogColumnId.WINS_AT,
                CommitmentDetailsDialogColumnId.STATUS,
                CommitmentDetailsDialogColumnId.VIEW_COMMITMENT,
            ],
            goalCommitments: row.row.assessedGoalCommitments,
        };
        this._dialog.open(CommitmentDetailsDialogComponent, {
            movable: true,
            resizable: true,
            data,
        });
    }

    /** @private */
    _onViewExcludedCommitmentsClick(row: ViewRow): void {
        const data: CommitmentDetailsDialogData = {
            columns: [
                CommitmentDetailsDialogColumnId.DIRECT_REPORT,
                CommitmentDetailsDialogColumnId.START_DATE,
                CommitmentDetailsDialogColumnId.DAYS_ACTIVE,
                CommitmentDetailsDialogColumnId.METRIC,
                CommitmentDetailsDialogColumnId.BEHAVIOUR,
                CommitmentDetailsDialogColumnId.DISPOSITION,
                CommitmentDetailsDialogColumnId.STATUS,
                CommitmentDetailsDialogColumnId.VIEW_COMMITMENT,
            ],
            goalCommitments: row.row.excludedGoalCommitments,
        };
        this._dialog.open(CommitmentDetailsDialogComponent, {
            movable: true,
            resizable: true,
            data,
        });
    }

    /** @private */
    _onInfoClick(): void {
        this._dialog.open(InfoDialogComponent, { movable: true });
    }
}
